import {
  GetContextMenuItemsParams,
  GridApi,
  MenuItemDef,
  RowNode,
} from '@ag-grid-community/core'
import { Injectable } from '@angular/core'
import { isColorDark } from '@helios/utils'
import { AgGridApi } from '@northfork/ag-grid'
import { dspTokens } from '@northfork/dsp'

//PVAL-11368
export enum ContextualLabelingColors {
  violet = '#EE82EE',
  aquamarine = '#7FFFD4',
  khaki = '#F0E68C',
  hotpink = '#FF69B4',
  skyblue = '#87CEFA',
  coral = '#FF7f50',
  lightgreen = '#90EE90',
  mediumpurple = '#A486E1',
}

export enum ContextualHexToLabelColors {
  '#EE82EE' = 'violet',
  '#7FFFD4' = 'aquamarine',
  '#F0E68C' = 'khaki',
  '#FF69B4' = 'hotpink',
  '#87CEFA' = 'skyblue',
  '#FF7f50' = 'coral',
  '#90EE90' = 'lightgreen',
  '#A486E1' = 'mediumpurple',
}

const colors = Object.values(ContextualLabelingColors)
const colorsWithFontColors = colors.map(x => ({
  backgroundColor: x,
  color: isColorDark(x) ? dspTokens.color['gray-400'].value : undefined,
}))

export const row = 'row'

interface HighlightIdentifiers {
  rowIds: string[]
  columnId: string
}

export type Highlighted = Record<string, Record<string, string>>

/**
 * using this interface to get out of a circular dependency with user preferences.
 */
export interface HighlightsStorage {
  getValue(key: string): Promise<Highlighted | undefined>
  setValue(key: string, value: Highlighted): Promise<void>
}

/**
 * Usage:
 *
 * Since by default services are singletons, we need to inject them into components,
 * scoped to the components. To do this, you'll add the import in the component declaration.
 *
 * Example:
 *
 * @ Component({
 * ...
 *  providers: [HighlightingService]
 * })
 */
@Injectable()
export class HighlightingService {
  /** @internal */
  highlighted: Highlighted = {}
  private storageKey?: string
  private storage?: HighlightsStorage

  /**
   * Check if the grid has highlights
   */
  hasHighlights() {
    return Object.keys(this.highlighted).length > 0
  }

  /**
   * Check if the specific row has highlights
   */
  hasRowHighlights(id: string) {
    return Object.prototype.hasOwnProperty.call(this.highlighted, id)
  }

  /**
   * Clear all highlights from the grid
   */
  async clearAll() {
    this.highlighted = {}
    await this.setHighlightedInStorage()
  }

  /**
   * Set the highlight styles for the row
   */
  getRowClass(rowId?: string) {
    if (!rowId) {
      return ''
    }

    const highlightedRow = this.highlighted[rowId] ?? {}
    const hex = highlightedRow[row]
    // Annoying typescript to get dynamically lookup the key
    const color = (
      ContextualHexToLabelColors as unknown as Record<string, string>
    )[hex]
    return color ? `highlighted ${color}` : ''
  }

  /**
   * Set the highlight styles for the cell
   */
  getCellStyle(rowId: string, colId: string) {
    const highlightedRow = this.highlighted[rowId] ?? {}
    const color = highlightedRow[colId]
    return colorsWithFontColors.find(x => x.backgroundColor === color) ?? {}
  }

  /**
   * Remove the row highlights
   */
  async removeRowHighlighting(api: AgGridApi | undefined, id: string) {
    const { [id]: deletedProp, ...remainder } = this.highlighted
    this.highlighted = { ...remainder }
    const node = api?.api.getRowNode(id)
    if (node != null) {
      api?.api.redrawRows({ rowNodes: [node] })
    }
    await this.setHighlightedInStorage()
  }

  /**
   * Pull the identifiers out from the grid parameters
   */
  private getIdentifiers(
    params: GetContextMenuItemsParams,
  ): HighlightIdentifiers {
    const columnId = params?.column?.getColDef?.call(params.column)?.field ?? ''
    const nodes = params?.api?.getSelectedNodes?.call(params.api) ?? []
    const clickedRowId = params.node?.id ?? ''
    let rowIds = nodes.map(x => x.id ?? '')
    const noRowsSelected = rowIds.length === 0

    /**
     * If no rows are selected, then we'll highlight the row that was clicked
     * If only one row is selected, then we'll highlight the row that was clicked
     */
    if (noRowsSelected || (rowIds.length === 1 && rowIds[0] !== clickedRowId)) {
      rowIds = [clickedRowId]
    }

    return {
      columnId,
      rowIds,
    }
  }
  /**
   * Pull the identifiers out from the AgGridApi parameters
   */
  getIdentifiersFromGridApi(gridApi: AgGridApi): HighlightIdentifiers {
    const displayedColumns = gridApi.columnApi.getAllDisplayedColumns()
    const columnId = displayedColumns[0].getColDef().field ?? ''
    const nodes = gridApi.api.getSelectedNodes.call(gridApi.api) ?? []
    const rowIds = nodes.map(x => x.id ?? '')
    return { columnId, rowIds }
  }

  /**
   * Action for removing a highlight
   */
  private async onRemoveHighlight(
    isRow: boolean,
    identifiers: HighlightIdentifiers,
    gridApi: GridApi,
  ) {
    const rowNodes: RowNode[] = []
    for (const id of identifiers.rowIds) {
      let highlightSet = this.highlighted[id]
      if (!highlightSet) {
        continue
      }

      const {
        [isRow ? 'row' : identifiers.columnId]: deletedProp,
        ...remainder
      } = highlightSet
      highlightSet = { ...remainder }

      if (Object.keys(highlightSet).length === 0) {
        const { [id]: deletedPropHighlighted, ...remainderHighlighted } =
          this.highlighted
        this.highlighted = {
          ...remainderHighlighted,
        }
      }

      const node = gridApi.getRowNode(id)
      if (node) {
        rowNodes.push(node)
      }
    }

    gridApi?.redrawRows({ rowNodes })
    await this.setHighlightedInStorage()
  }

  /**
   * Action for setting the highlight
   */
  private async onSetHighlight(
    isRow: boolean,
    identifiers: HighlightIdentifiers,
    gridApi: GridApi,
    color: string,
  ) {
    const rowNodes: RowNode[] = []
    for (const id of identifiers.rowIds) {
      const settings = this.highlighted[id] ?? {}
      settings[isRow ? row : identifiers.columnId] = color
      this.highlighted[id] = settings

      const node = gridApi.getRowNode(id)
      if (node) {
        rowNodes.push(node)
      }
    }

    gridApi?.redrawRows({ rowNodes })
    await this.setHighlightedInStorage()
  }
  setHighlightOnExternalAction(
    selectedRows: AgGridApi | undefined,
    highlightColorNumber: number,
  ) {
    if (selectedRows) {
      const identifiers = this.getIdentifiersFromGridApi(selectedRows)
      const colorKeys = Object.keys(ContextualLabelingColors)
      const color =
        ContextualLabelingColors[
          colorKeys[
            highlightColorNumber
          ] as keyof typeof ContextualLabelingColors
        ]
      this.onSetHighlight(true, identifiers, selectedRows.api, color)
    }
  }

  /**
   * @internal
   * Create the grid items for the context menu
   */
  createHighlightMenuItem(
    isRow: boolean,
    params: GetContextMenuItemsParams,
    excludedColors: ContextualLabelingColors[] = [],
  ): MenuItemDef {
    const identifiers = this.getIdentifiers(params)

    return {
      name: isRow ? 'Highlight Row' : 'Highlight Cell',
      subMenu: [
        {
          name: 'Remove Highlight',
          cssClasses: ['selection-color'],
          icon: `<span class="selection-color-swatch blank"></span>`,
          action: () => this.onRemoveHighlight(isRow, identifiers, params.api),
        },
        ...colors
          .filter(color => !excludedColors.includes(color))
          .map(
            color =>
              ({
                name: color,
                cssClasses: ['selection-color'],
                icon: `<span class="selection-color-swatch" style="background-color: ${color}"></span>`,
                action: () =>
                  this.onSetHighlight(isRow, identifiers, params.api, color),
              } as MenuItemDef),
          ),
      ],
    }
  }

  createRemoveHighlightingMenuItem(
    params: GetContextMenuItemsParams,
  ): MenuItemDef {
    const identifiers = this.getIdentifiers(params)
    const someHighlighted = identifiers.rowIds.some(id =>
      this.hasRowHighlights(id),
    )

    return {
      name: 'Remove Highlighting',
      disabled: !someHighlighted,
      tooltip: someHighlighted
        ? 'Removes all highlights from all selected rows.'
        : 'None of the selected rows have highlights!',
      action: () =>
        identifiers.rowIds.forEach(id =>
          this.removeRowHighlighting(params, id),
        ),
    }
  }

  createHighlightMenuItems(
    params: GetContextMenuItemsParams,
    highlightTypes: 'Cell' | 'Row' | 'Both' = 'Both',
    excludedColors: ContextualLabelingColors[] = [],
  ): MenuItemDef[] {
    const items: MenuItemDef[] = []
    switch (highlightTypes) {
      case 'Row':
        items.push(this.createHighlightMenuItem(true, params, excludedColors))
        break
      case 'Cell':
        items.push(this.createHighlightMenuItem(false, params, excludedColors))
        break
      default:
        items.push(this.createHighlightMenuItem(true, params, excludedColors))
        items.push(this.createHighlightMenuItem(false, params, excludedColors))
        break
    }

    items.push(this.createRemoveHighlightingMenuItem(params))
    return items
  }

  /**
   * If dependents use this highlighting will be saved in the cloud.
   *
   * @param gridKey
   */
  async setGridKeyForPersistentStorage(
    gridKey: string,
    storage: HighlightsStorage,
    gridApi: GridApi,
  ) {
    this.storageKey = this.getHighlightsKey(gridKey)
    this.storage = storage
    await this.setHighlightedFromStorage(this.storage, this.storageKey)
    this.redrawAllHighlightedRows(gridApi)
  }

  /**
   * @internal
   * @param storage
   * @param storageKey
   */
  async setHighlightedFromStorage(
    storage: HighlightsStorage,
    storageKey: string,
  ): Promise<void> {
    const storedHighlights = await storage.getValue(storageKey)
    if (storedHighlights == null || Object.keys(storedHighlights).length === 0)
      return
    if (this.hasHighlights()) {
      this.highlighted = {
        ...storedHighlights,
        ...this.highlighted,
      }
      await this.setHighlightedInStorage()
    } else {
      this.highlighted = storedHighlights
    }
  }

  /**
   * @internal
   * @param gridApi
   * @private
   */
  redrawAllHighlightedRows(gridApi: GridApi) {
    const rowNodes: RowNode[] = []

    for (const key of Object.keys(this.highlighted)) {
      const node = gridApi.getRowNode(key)
      if (node == null) continue
      rowNodes.push(node)
    }

    gridApi.redrawRows({ rowNodes })
  }

  private getHighlightsKey(gridKey: string) {
    return `${gridKey}-row-highlights`
  }

  private async setHighlightedInStorage(): Promise<void> {
    if (this.storageKey == null || this.storage == null) return
    await this.storage.setValue(this.storageKey, this.highlighted)
  }
}
